<script setup>
import { computed, ref, watch } from 'vue'
import { api, asList } from '../api/client'
import { bookingMoneyCents, formatHistoryMoney } from '../api/booking-history'
import { formatDateTime } from '../utils'
import SaleDialog from './SaleDialog.vue'
import SaleCancelDialog from './SaleCancelDialog.vue'

const props = defineProps({
  bookingId: { type: [Number, String], required: true },
  bookingStatus: { type: String, default: '' },
  lodgingAmount: { type: [String, Number], default: 0 },
})
const emit = defineEmits(['changed'])
const sales = ref([])
const catalog = ref([])
const loading = ref(false)
const error = ref('')
const saleOpen = ref(false)
const saving = ref(false)
const saleError = ref('')
const cancellingSale = ref(null)
const cancelling = ref(false)
const cancelError = ref('')
let sequence = 0

const extrasCents = computed(() => sales.value
  .filter((sale) => sale.status !== 'cancelled')
  .reduce((sum, sale) => sum + (bookingMoneyCents(sale.total_amount) || 0), 0))
const extrasAmount = computed(() => (extrasCents.value / 100).toFixed(2))
const grandTotal = computed(() => (((bookingMoneyCents(props.lodgingAmount || 0) || 0) + extrasCents.value) / 100).toFixed(2))

async function load() {
  const requestSequence = ++sequence
  loading.value = true
  error.value = ''
  try {
    const [rows, items] = await Promise.all([
      api.sales.list({ booking_id: props.bookingId }), api.catalog.list(),
    ])
    if (requestSequence === sequence) {
      sales.value = asList(rows)
      catalog.value = asList(items)
    }
  } catch (requestError) {
    if (requestSequence === sequence) error.value = requestError.message || 'Не удалось загрузить продажи брони.'
  } finally {
    if (requestSequence === sequence) loading.value = false
  }
}

async function createSale(payload) {
  if (saving.value) return
  saving.value = true
  saleError.value = ''
  try {
    await api.sales.create({ ...payload, booking_id: Number(props.bookingId) })
    saleOpen.value = false
    emit('changed')
    await load()
  } catch (requestError) {
    saleError.value = requestError.message || 'Не удалось провести продажу.'
  } finally {
    saving.value = false
  }
}

async function cancelSale(reason) {
  if (!cancellingSale.value || cancelling.value) return
  cancelling.value = true
  cancelError.value = ''
  try {
    await api.sales.cancel(cancellingSale.value.id, reason)
    cancellingSale.value = null
    emit('changed')
    await load()
  } catch (requestError) {
    cancelError.value = requestError.message || 'Не удалось отменить продажу.'
  } finally {
    cancelling.value = false
  }
}

function openSale() {
  saleError.value = ''
  saleOpen.value = true
}

function openCancel(sale) {
  cancelError.value = ''
  cancellingSale.value = sale
}

watch(() => props.bookingId, load, { immediate: true })
</script>

<template>
  <section class="booking-sales" aria-labelledby="booking-sales-heading">
    <header class="section-header">
      <div>
        <h3 id="booking-sales-heading">Услуги и товары в брони</h3>
        <p>Продажи проводятся сразу. Записи не удаляются: ошибочную продажу нужно отменить.</p>
      </div>
      <button type="button" class="button button--secondary" :disabled="loading || !!error || bookingStatus === 'cancelled'" @click="openSale">Добавить услугу или товар</button>
    </header>
    <p v-if="loading" role="status">Загружаем продажи…</p>
    <div v-if="error" role="alert">
      <p class="form-error">{{ error }}</p>
      <button type="button" class="button button--ghost" @click="load">Повторить загрузку продаж</button>
    </div>
    <p v-if="!loading && !error && !sales.length" class="booking-sales__hint">Дополнительных услуг и товаров пока нет.</p>
    <ul v-if="sales.length" class="booking-sales__list">
      <li v-for="sale in sales" :key="sale.id" :class="{ 'booking-sales__cancelled': sale.status === 'cancelled' }">
        <div class="booking-sales__header">
          <strong>Продажа №{{ sale.id }} · {{ formatHistoryMoney(sale.total_amount) }}</strong>
          <span>{{ sale.status === 'cancelled' ? 'Отменена' : 'Проведена' }}</span>
        </div>
        <p class="booking-sales__hint">{{ formatDateTime(sale.created_at) }} · {{ sale.created_by?.full_name || sale.created_by?.username || 'Сотрудник' }}</p>
        <ul class="booking-sales__lines">
          <li v-for="line in sale.lines" :key="line.id || line.item_id">{{ line.item_name }} — {{ line.quantity }} {{ line.item_unit }} × {{ formatHistoryMoney(line.unit_price) }} = {{ formatHistoryMoney(line.total_amount) }}</li>
        </ul>
        <p v-if="sale.notes" class="booking-sales__note">{{ sale.notes }}</p>
        <p v-if="sale.status === 'cancelled'" class="booking-sales__note">Отмена: {{ sale.cancel_reason }} · {{ sale.cancelled_by?.full_name || sale.cancelled_by?.username || 'Сотрудник' }} · {{ formatDateTime(sale.cancelled_at) }}</p>
        <button v-else type="button" class="button button--ghost booking-sales__cancel" @click="openCancel(sale)">Отменить продажу №{{ sale.id }}</button>
      </li>
    </ul>
    <dl v-if="!error && !loading" class="booking-sales__totals">
      <div><dt>Дополнительные услуги и товары</dt><dd>{{ formatHistoryMoney(extrasAmount) }}</dd></div>
      <div><dt>Размещение + активные продажи</dt><dd>{{ formatHistoryMoney(grandTotal) }}</dd></div>
    </dl>
    <p class="booking-sales__hint">Стоимость размещения и предоплата редактируются отдельно. Отмена брони не отменяет уже проведённые продажи: отмените их отдельно, если нужен возврат товара.</p>
    <SaleDialog :open="saleOpen" :catalog="catalog" :booking-id="Number(bookingId)" :saving="saving" :error="saleError" @save="createSale" @close="!saving && (saleOpen = false)" />
    <SaleCancelDialog :open="!!cancellingSale" :sale="cancellingSale" :pending="cancelling" :error="cancelError" @confirm="cancelSale" @close="!cancelling && (cancellingSale = null)" />
  </section>
</template>

<style scoped>
.booking-sales { border-top: 1px solid var(--line); margin-top: 24px; padding-top: 24px; }
.booking-sales .section-header { gap: 14px; align-items: flex-start; }
.booking-sales h3 { margin-bottom: 6px; }
.booking-sales .section-header p, .booking-sales__hint { font-size: .76rem; color: var(--muted); line-height: 1.5; }
.booking-sales__list, .booking-sales__lines { list-style: none; padding: 0; margin: 12px 0; }
.booking-sales__list { display: grid; gap: 12px; }
.booking-sales__list > li { border: 1px solid var(--line); border-radius: 10px; padding: 16px; background: var(--paper-strong); }
.booking-sales__header { display: flex; justify-content: space-between; gap: 10px; font-size: .82rem; }
.booking-sales__header > span { white-space: nowrap; color: var(--forest-soft); }
.booking-sales__cancelled .booking-sales__header > span { color: var(--danger); }
.booking-sales__cancelled { opacity: .78; }
.booking-sales__lines { font-size: .79rem; display: grid; gap: 6px; }
.booking-sales__note { font-size: .78rem; white-space: pre-wrap; overflow-wrap: anywhere; }
.booking-sales__cancel { color: var(--danger); padding-left: 0; }
.booking-sales__totals { font-size: .82rem; border-top: 1px solid var(--line); padding-top: 12px; }
.booking-sales__totals > div { display: flex; justify-content: space-between; gap: 12px; margin: 8px 0; }
.booking-sales__totals dd { margin: 0; font-weight: 700; white-space: nowrap; }
@media (max-width: 600px) { .booking-sales .section-header { flex-direction: column; } .booking-sales__header { flex-direction: column; } }
</style>
